// ViewAsDropdown - Admin "View As" user selector for impersonation

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService, type ViewAsUser } from '../../services/auth';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { getDefaultLandingPage } from '../../utils/landingPage';
import styles from './ViewAsDropdown.module.css';

export function ViewAsDropdown() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<ViewAsUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchUsers = useCallback(async () => {
    if (users.length > 0) return; // already loaded
    setLoading(true);
    setError(null);
    try {
      const result = await authService.listUsersForViewAs();
      setUsers(result);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [users.length]);

  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) fetchUsers();
  };

  const handleSelect = async (userId: string) => {
    setIsOpen(false);
    try {
      // Land where that person lands when they sign in themselves (an
      // executive on the roadmap, a CSR on the inbox, Jess on the Quality
      // Co-Pilot), not on the CSR inbox for everyone.
      const viewed = await impersonate(userId);
      navigate(getDefaultLandingPage(viewed));
    } catch {
      setError('Failed to switch user');
    }
  };

  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, close, isOpen);
  useEscapeKey(close, isOpen);

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <button
        className={styles.trigger}
        onClick={handleToggle}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        View As
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.menu} role="listbox">
          {loading && <div className={styles.menuMessage}>Loading users...</div>}
          {error && <div className={styles.menuError}>{error}</div>}
          {!loading && !error && users.length === 0 && (
            <div className={styles.menuMessage}>No users found</div>
          )}
          {users.map((u) => (
            <button
              key={u.id}
              className={styles.menuItem}
              onClick={() => handleSelect(u.id)}
              type="button"
              role="option"
            >
              <span className={styles.userName}>{u.name}</span>
              <span className={styles.userMeta}>
                {u.role}{u.salesperson_id ? ` (${u.salesperson_id})` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
